/**
 * Whether a port's value is LIVE, or how old it is (the walk of 2026-09-26: a month-old imported furnace temperature
 * showed under "Live parameters" with a green dot). A value is live only when its reading is at most LIVE_WITHIN_MS
 * old; an older one says its age, and a value with no readable time is never live. A reading up to MAX_CLOCK_SKEW_MS
 * ahead of this clock (another machine's skew) counts as live; one further ahead is not, and says how far. Pure.
 */

/** How recent a reading must be to count as live. */
export const LIVE_WITHIN_MS = 15 * 60_000;

/** How far ahead of this clock a reading may be stamped and still count as live. */
export const MAX_CLOCK_SKEW_MS = 5 * 60_000;

const DAY_MS = 24 * 3_600_000;

export interface PortFreshness {
  /** The reading is fresh enough to be called live. */
  live: boolean;
  /** For a reading that is not live: "40m ago", "5h ago" or "as of 26 Aug 2026". Null when live or with no time. */
  asOf: string | null;
}

export interface PortFreshnessOptions {
  /** Formats a reading older than a day as a date; the page passes the viewer's locale. */
  formatDate?: (iso: string) => string;
}

const defaultDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export function portFreshness(
  time: string | undefined,
  now: number,
  opts: PortFreshnessOptions = {},
): PortFreshness {
  const at = time ? Date.parse(time) : NaN;
  if (!Number.isFinite(at)) return { live: false, asOf: null };
  const age = now - at;
  if (age < -MAX_CLOCK_SKEW_MS) {
    const ahead = -age;
    if (ahead < 3_600_000) return { live: false, asOf: `stamped ${Math.round(ahead / 60_000)}m ahead` };
    if (ahead < DAY_MS) return { live: false, asOf: `stamped ${Math.round(ahead / 3_600_000)}h ahead` };
    return { live: false, asOf: `stamped ${(opts.formatDate ?? defaultDate)(time as string)}` };
  }
  if (age <= LIVE_WITHIN_MS) return { live: true, asOf: null };
  if (age < 3_600_000) return { live: false, asOf: `${Math.round(age / 60_000)}m ago` };
  if (age < DAY_MS) return { live: false, asOf: `${Math.round(age / 3_600_000)}h ago` };
  return { live: false, asOf: `as of ${(opts.formatDate ?? defaultDate)(time as string)}` };
}
